// Route: /flashcards/sets/[id] — workspace набора карточек.
// URL state: ?demo=loading|empty|error|streaming
//
// См. docs/ui-spec/flashcards.sets.[id].{desktop,mobile}.md.

import { tolerantSearch } from "@studyhub/shared/utils/tolerant-search";
import { Button } from "@studyhub/ui/components/button";
import { EmptyState } from "@studyhub/ui/components/empty-state";
import { ErrorState } from "@studyhub/ui/components/error-state";
import { Input } from "@studyhub/ui/components/input";
import { Label } from "@studyhub/ui/components/label";
import {
	ResponsiveDialog,
	ResponsiveDialogContent,
	ResponsiveDialogDescription,
	ResponsiveDialogFooter,
	ResponsiveDialogHeader,
	ResponsiveDialogTitle,
} from "@studyhub/ui/components/responsive-dialog";
import { Skeleton } from "@studyhub/ui/components/skeleton";
import { CategoryTile } from "@studyhub/ui/components/studyhub/category-tile";
import { IconTooltip } from "@studyhub/ui/components/tooltip";
import { cn } from "@studyhub/ui/lib/cn";
import { toast } from "@studyhub/ui/toast";
import {
	IconArrowLeft,
	IconCards,
	IconDotsVertical,
	IconEdit,
	IconExternalLink,
	IconFileExport,
	IconPalette,
	IconPlus,
	IconSparkles,
	IconTrash,
} from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate, useParams } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
	type FlashcardDto,
	type FlashcardType,
	flashcardSetHasReadyData,
	type SrsState,
	useDeleteFlashcard,
	useDeleteFlashcardSet,
	useFlashcardSetDetail,
	useReorderFlashcards,
	useToggleFlashcardMark,
	useUpdateFlashcardSet,
} from "@/components/flashcards/_api";
import { GenerateDialog } from "@/components/flashcards/library/generate-dialog";
import { CardEditorDialog } from "@/components/flashcards/set-detail/card-editor-dialog";
import { CardsTable } from "@/components/flashcards/set-detail/cards-table";
import { SetDetailFilters } from "@/components/flashcards/set-detail/filters-bar";
import { StatsWidget } from "@/components/flashcards/set-detail/stats-widget";
import { ActionMenu, type ActionMenuItem } from "@/components/shared/action-menu";
import { LIST_PAGE_SHELL, ListToolbar } from "@/components/shared/entity-list";
import { OfflineDetailState } from "@/components/shared/offline-detail-state";
import { OfflineNotice } from "@/components/shared/offline-notice";
import { useArtifactWatching } from "@/hooks/use-artifact-watching";
import { markArtifactSeenKey, useMarkArtifactSeen } from "@/hooks/use-mark-artifact-seen";
import { useOnlineStatus } from "@/hooks/use-online-status";
import { useSocketEvent } from "@/hooks/use-socket-event";
import { useSyncedDetail } from "@/hooks/use-synced-detail";
import { fromSearchSchema, resolveBackFrom } from "@/lib/back-from";
import { IS_MOBILE_PLATFORM } from "@/lib/platform-detect";

const SET_COLORS = [
	"#6366F1",
	"#F59E0B",
	"#10B981",
	"#A855F7",
	"#EC4899",
	"#06B6D4",
	"#EF4444",
	"#84CC16",
];

/** Экранирование значения для CSV (RFC 4180). */
function csvCell(v: string): string {
	return /[",\n]/.test(v) ? `"${v.replaceAll('"', '""')}"` : v;
}

function exportCardsCsv(setName: string, cards: FlashcardDto[]): void {
	const header = ["type", "front", "back", "hint", "tags"];
	const rows = cards.map((c) =>
		[c.type, c.front, c.back, c.hint ?? "", c.tags.join("|")].map(csvCell).join(","),
	);
	const csv = [header.join(","), ...rows].join("\n");
	const blob = new Blob([`﻿${csv}`], { type: "text/csv;charset=utf-8" });
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = `${setName || "flashcards"}.csv`;
	a.click();
	URL.revokeObjectURL(url);
}

/** Пункты меню действий набора (используется ActionMenu — desktop dropdown / mobile шторка). */
function buildSetMenuItems(args: {
	t: (key: string) => string;
	onRename: () => void;
	onColor: () => void;
	onRegenerate: () => void;
	onExport: () => void;
	sourceMaterialId?: string;
	onOpenMaterial?: () => void;
	onDelete: () => void;
}): ActionMenuItem[] {
	const { t } = args;
	const items: ActionMenuItem[] = [
		{
			key: "rename",
			label: t("setDetail.menu.rename"),
			icon: <IconEdit />,
			onSelect: args.onRename,
		},
		{
			key: "color",
			label: t("setDetail.menu.color"),
			icon: <IconPalette />,
			onSelect: args.onColor,
		},
		{
			key: "regenerate",
			label: t("setDetail.menu.regenerate"),
			icon: <IconSparkles />,
			onSelect: args.onRegenerate,
		},
		{
			key: "export",
			label: t("setDetail.menu.exportCsv"),
			icon: <IconFileExport />,
			onSelect: args.onExport,
		},
	];
	if (args.sourceMaterialId && args.onOpenMaterial) {
		items.push({
			key: "openMaterial",
			label: t("setDetail.menu.openMaterial"),
			icon: <IconExternalLink />,
			onSelect: args.onOpenMaterial,
		});
	}
	items.push({
		key: "delete",
		label: t("setDetail.menu.delete"),
		icon: <IconTrash />,
		tone: "destructive",
		separatorBefore: true,
		onSelect: args.onDelete,
	});
	return items;
}

const searchSchema = z
	.object({
		demo: z.enum(["default", "loading", "empty", "error", "streaming"]).optional(),
		type: z.string().optional(),
		srs: z.string().optional(),
		q: z.string().optional(),
	})
	.merge(fromSearchSchema);

export const Route = createFileRoute("/flashcards/sets/$setId")({
	validateSearch: tolerantSearch(searchSchema),
	component: SetDetailPage,
});

function SetDetailPage(): React.ReactElement {
	const { t } = useTranslation("flashcards");
	const { t: tTrash } = useTranslation("trash");
	const online = useOnlineStatus();
	const { setId } = useParams({ from: "/flashcards/sets/$setId" });
	const search = Route.useSearch();
	const navigate = useNavigate({ from: Route.fullPath });
	useArtifactWatching(markArtifactSeenKey("flashcards", setId));
	const backOrigin = resolveBackFrom(search.from);
	const goBack = () => {
		if (backOrigin?.kind === "material") {
			void navigate({
				to: "/materials/$materialId",
				params: { materialId: backOrigin.materialId },
			});
		} else if (backOrigin?.kind === "subsection") {
			void navigate({
				to: "/study-plan/$id/subsection/$subId",
				params: { id: backOrigin.planId, subId: backOrigin.subsectionId },
			});
		} else {
			void navigate({ to: "/flashcards" });
		}
	};

	const [editorOpen, setEditorOpen] = useState(false);
	const [editorCard, setEditorCard] = useState<FlashcardDto | undefined>(undefined);
	const [generateOpen, setGenerateOpen] = useState(false);
	const [searchQ, setSearchQ] = useState(search.q ?? "");
	const [renameOpen, setRenameOpen] = useState(false);
	const [renameValue, setRenameValue] = useState("");
	const [deleteOpen, setDeleteOpen] = useState(false);
	const [colorOpen, setColorOpen] = useState(false);

	const detailQuery = useFlashcardSetDetail(setId);
	const qc = useQueryClient();
	const markSeen = useMarkArtifactSeen();
	const updateSet = useUpdateFlashcardSet();
	const deleteSet = useDeleteFlashcardSet();
	const deleteCard = useDeleteFlashcard();
	const toggleCardMark = useToggleFlashcardMark();
	const reorderCards = useReorderFlashcards();

	// Terminal generation events → refetch detail (poll — fallback, см. _api).
	useSocketEvent("flashcards:ready", (payload) => {
		if (payload.setId !== setId) return;
		void qc.invalidateQueries({ queryKey: ["flashcard-sets", "get", setId] });
		void qc.invalidateQueries({ queryKey: ["flashcards", "list", setId] });
		markSeen(markArtifactSeenKey("flashcards", setId), "surface");
	});
	useSocketEvent("flashcards:generation_failed", (payload) => {
		if (payload.setId !== setId) return;
		void qc.invalidateQueries({ queryKey: ["flashcard-sets", "get", setId] });
		void qc.invalidateQueries({ queryKey: ["flashcards", "list", setId] });
		toast.error(t("library.streaming.failed"));
	});

	const isLoading = search.demo === "loading" || detailQuery.isLoading;
	const isError = search.demo === "error" || detailQuery.isError;
	const set = detailQuery.data?.set;
	const stats = detailQuery.data?.stats;
	const allCards = detailQuery.data?.cards ?? [];
	// Набор может ещё не приехать в локальную SQLite (cold start, свежий
	// аккаунт) — раньше в этом случае рендерилась только «липкая» шапка, а тело
	// страницы было `null`: пустой экран без единого объяснения.
	const detailState = useSyncedDetail({
		hasData:
			Boolean(set && stats) &&
			flashcardSetHasReadyData({
				cardCount: allCards.length,
				remoteStatus: detailQuery.remoteStatus,
				processingStatus: set?.processingStatus,
			}),
		localLoading: detailQuery.isLoading,
		localError: detailQuery.isError,
		remoteStatus: detailQuery.remoteStatus,
	});
	const stillGenerating =
		set?.processingStatus === "generating" && (set.cardCount === 0 || allCards.length === 0);

	const filteredCards = useMemo(() => {
		let items = allCards;
		if (search.demo === "empty") items = [];
		if (search.type) items = items.filter((c) => c.type === (search.type as FlashcardType));
		if (search.srs) items = items.filter((c) => c.srsState === (search.srs as SrsState));
		if (searchQ) {
			const q = searchQ.toLowerCase();
			items = items.filter(
				(c) => c.front.toLowerCase().includes(q) || c.back.toLowerCase().includes(q),
			);
		}
		return items;
	}, [allCards, search, searchQ]);

	const filterControls = (stacked: boolean) => (
		<SetDetailFilters
			stacked={stacked}
			type={search.type}
			srs={search.srs}
			onTypeChange={(type) =>
				void navigate({ search: (prev) => ({ ...prev, type }), replace: true })
			}
			onSrsChange={(srs) => void navigate({ search: (prev) => ({ ...prev, srs }), replace: true })}
		/>
	);

	return (
		<div className={LIST_PAGE_SHELL}>
			{/* Sticky header */}
			<header className="sticky top-[var(--topbar-height)] z-20 -mx-4 flex items-center gap-3 border-b border-border bg-background/95 px-4 py-3 backdrop-blur-sm lg:-mx-6 lg:px-6">
				<IconTooltip label={t("setDetail.back")}>
					<Button
						variant="ghost"
						size="icon"
						className="shrink-0"
						onClick={goBack}
						aria-label={t("setDetail.back")}
					>
						<IconArrowLeft size={18} />
					</Button>
				</IconTooltip>
				{isLoading ? (
					<Skeleton className="h-7 w-48" />
				) : set ? (
					<>
						<div className="relative shrink-0">
							<CategoryTile category="cards" icon={<IconCards />} size="md" />
							<span
								className="absolute -right-0.5 -top-0.5 size-2.5 rounded-full border-2 border-background"
								style={{ backgroundColor: set.color }}
								aria-hidden
							/>
						</div>
						<div className="min-w-0 flex-1">
							<h1 className="truncate text-lg font-semibold">{set.name}</h1>
							{set.description && (
								<p className="truncate text-xs text-muted-foreground">{set.description}</p>
							)}
						</div>
						<Button asChild disabled={set.cardCount === 0} className="shrink-0">
							<Link to="/flashcards/study/$setId" params={{ setId: set.id }}>
								<IconCards size={16} />
								{/* На mobile — короткая подпись + due-бейдж, чтобы не теснить title. */}
								<span className="hidden lg:inline">
									{t("setDetail.studyCta", { due: set.dueCount })}
								</span>
								<span className="lg:hidden">{t("mobileUx.studyCtaShort")}</span>
								{set.dueCount > 0 && (
									<span className="inline-flex min-w-4 items-center justify-center rounded-full bg-primary-foreground/20 px-1 text-2xs tabular-nums lg:hidden">
										{set.dueCount}
									</span>
								)}
							</Link>
						</Button>
						<ActionMenu
							align="end"
							trigger={
								<Button variant="ghost" size="icon" className="shrink-0 cursor-pointer">
									<IconDotsVertical size={18} />
								</Button>
							}
							items={buildSetMenuItems({
								t,
								onRename: () => {
									setRenameValue(set.name);
									setRenameOpen(true);
								},
								onColor: () => setColorOpen(true),
								onRegenerate: () => setGenerateOpen(true),
								onExport: () => exportCardsCsv(set.name, allCards),
								sourceMaterialId: set.sourceMaterialId ?? undefined,
								onOpenMaterial: set.sourceMaterialId
									? () =>
											void navigate({
												to: "/materials/$materialId",
												params: { materialId: set.sourceMaterialId ?? "" },
											})
									: undefined,
								onDelete: () => setDeleteOpen(true),
							})}
						/>
					</>
				) : null}
			</header>

			{/* Subtitle row */}
			{set && stats && (
				<p className="text-xs text-muted-foreground tabular-nums">
					{t("setDetail.subtitle", {
						total: stats.totalCount,
						due: stats.dueCount,
						new: stats.byState.new,
						mastery: Math.round(stats.accuracy * 100),
					})}
				</p>
			)}

			{/* Body */}
			{isLoading || detailState.isLoading ? (
				<div className="space-y-4">
					<Skeleton className="h-36 w-full rounded-[var(--radius-panel)]" />
					<Skeleton className="h-96 w-full rounded-[var(--radius-card)]" />
				</div>
			) : isError || detailState.isFailed ? (
				<ErrorState
					title={t("library.errorTitle")}
					description={t("library.errorSubtitle")}
					action={
						<Button variant="ghost" onClick={() => void detailQuery.refetch()}>
							{t("library.retry")}
						</Button>
					}
				/>
			) : detailState.isOfflineWithoutCopy ? (
				<OfflineDetailState onRetry={() => void detailQuery.refetch()} />
			) : detailState.isNotFound || !set || !stats ? (
				<EmptyState
					icon={<IconCards size={48} stroke={1.5} className="text-muted-foreground" />}
					title={t("library.errorTitle")}
					description={t("library.errorSubtitle")}
					action={
						<Button variant="ghost" onClick={goBack} className="cursor-pointer">
							{t("setDetail.back")}
						</Button>
					}
				/>
			) : (
				<>
					{/* Streaming banner — только пока карточек ещё нет */}
					{stillGenerating && (
						<div className="flex items-center gap-3 rounded-[var(--radius-card)] border border-primary/30 bg-primary/5 p-3.5 shadow-e1">
							<span className="flex size-9 shrink-0 items-center justify-center rounded-[var(--radius-tile)] tint-indigo">
								<IconSparkles size={17} className="animate-pulse text-primary" />
							</span>
							<div className="min-w-0 flex-1">
								<p className="text-sm font-medium">{t("library.streaming.label")}</p>
								<div className="mt-2 h-1.5 overflow-hidden rounded-full bg-[var(--well)] [box-shadow:inset_0_1px_2px_rgb(var(--shadow)/0.12)]">
									<div className="h-full w-2/5 rounded-full bg-linear-to-r from-primary to-primary-2" />
								</div>
							</div>
						</div>
					)}

					<StatsWidget stats={stats} />

					{/* Toolbar — единый ListToolbar (поиск + фильтры type/srs). Без grid/list:
					    карточки управляются drag-reorder-таблицей (CardsTable). Создание —
					    отдельной строкой действий над тулбаром. */}
					<div className="space-y-3">
						<div className="flex flex-wrap items-center gap-2">
							<Button
								type="button"
								size="sm"
								onClick={() => {
									setEditorCard(undefined);
									setEditorOpen(true);
								}}
								className="cursor-pointer pointer-coarse:h-11"
							>
								<IconPlus size={14} />
								{t("setDetail.toolbar.addCard")}
							</Button>
							<Button
								type="button"
								size="sm"
								variant="outline"
								onClick={() => setGenerateOpen(true)}
								className="cursor-pointer pointer-coarse:h-11"
							>
								<IconSparkles size={14} />
								{t("setDetail.toolbar.generate")}
							</Button>
						</div>

						<ListToolbar
							search={{
								value: searchQ,
								onChange: setSearchQ,
								placeholder: t("setDetail.toolbar.search"),
							}}
							filters={filterControls(false)}
							filtersSheet={filterControls(true)}
							filtersActive={Boolean(search.type || search.srs)}
							showViewToggle={false}
						/>
					</div>

					{/* Table or empty */}
					{filteredCards.length === 0 ? (
						<EmptyState
							icon={<IconCards size={48} stroke={1.5} className="text-muted-foreground" />}
							title={t("setDetail.empty.title")}
							description={t("setDetail.empty.subtitle")}
							action={
								<div className="flex flex-wrap gap-2 justify-center">
									<Button
										onClick={() => {
											setEditorCard(undefined);
											setEditorOpen(true);
										}}
										className="cursor-pointer"
									>
										<IconPlus size={14} /> {t("setDetail.toolbar.addCard")}
									</Button>
									<Button
										variant="outline"
										onClick={() => setGenerateOpen(true)}
										className="cursor-pointer"
									>
										<IconSparkles size={14} /> {t("setDetail.toolbar.generate")}
									</Button>
								</div>
							}
						/>
					) : (
						<CardsTable
							cards={filteredCards}
							onEdit={(card) => {
								setEditorCard(card);
								setEditorOpen(true);
							}}
							onDelete={(id) =>
								deleteCard.mutate(
									{ id, setId },
									{ onError: () => toast.error(t("library.errorTitle")) },
								)
							}
							onToggleMark={(id) => {
								const c = allCards.find((x) => x.id === id);
								if (!c) return;
								toggleCardMark.mutate(
									{ id, marked: !c.marked, setId },
									{ onError: () => toast.error(t("library.errorTitle")) },
								);
							}}
							onReorder={(orderedIds) =>
								reorderCards.mutate(
									{ setId, orderedIds },
									{ onError: () => toast.error(t("library.errorTitle")) },
								)
							}
						/>
					)}
				</>
			)}

			{/* Rename dialog */}
			<ResponsiveDialog open={renameOpen} onOpenChange={setRenameOpen}>
				<ResponsiveDialogContent className="sm:max-w-md">
					<ResponsiveDialogHeader>
						<ResponsiveDialogTitle>{t("setDetail.menu.rename")}</ResponsiveDialogTitle>
					</ResponsiveDialogHeader>
					<div className="space-y-1.5 py-2">
						<Label htmlFor="rename-set">{t("generate.nameLabel")}</Label>
						<Input
							id="rename-set"
							value={renameValue}
							onChange={(e) => setRenameValue(e.target.value)}
							autoFocus={!IS_MOBILE_PLATFORM}
						/>
					</div>
					<ResponsiveDialogFooter>
						<Button variant="ghost" onClick={() => setRenameOpen(false)} className="cursor-pointer">
							{t("generate.cancel")}
						</Button>
						<Button
							disabled={!renameValue.trim() || updateSet.isPending}
							onClick={() =>
								updateSet.mutate(
									{ id: setId, patch: { name: renameValue.trim() } },
									{
										onSuccess: () => setRenameOpen(false),
										onError: () => toast.error(t("library.errorTitle")),
									},
								)
							}
							className="cursor-pointer"
						>
							{t("editor.actions.save")}
						</Button>
					</ResponsiveDialogFooter>
				</ResponsiveDialogContent>
			</ResponsiveDialog>

			{/* Color dialog */}
			<ResponsiveDialog open={colorOpen} onOpenChange={setColorOpen}>
				<ResponsiveDialogContent className="sm:max-w-md">
					<ResponsiveDialogHeader>
						<ResponsiveDialogTitle>{t("setDetail.menu.color")}</ResponsiveDialogTitle>
					</ResponsiveDialogHeader>
					<div className="grid grid-cols-6 gap-3 py-2">
						{SET_COLORS.map((c) => (
							<button
								key={c}
								type="button"
								onClick={() =>
									updateSet.mutate(
										{ id: setId, patch: { color: c } },
										{
											onSuccess: () => setColorOpen(false),
											onError: () => toast.error(t("library.errorTitle")),
										},
									)
								}
								className={cn(
									"size-11 cursor-pointer rounded-full border-2 transition-transform hover:scale-110",
									set?.color === c ? "border-foreground scale-110" : "border-border",
								)}
								style={{ backgroundColor: c }}
								aria-label={t("library.colorPickerAria", { color: c })}
							/>
						))}
					</div>
				</ResponsiveDialogContent>
			</ResponsiveDialog>

			{/* Delete confirm dialog */}
			<ResponsiveDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
				<ResponsiveDialogContent className="min-w-0 sm:max-w-md">
					<ResponsiveDialogHeader>
						<ResponsiveDialogTitle>{t("setDetail.deleteConfirm.title")}</ResponsiveDialogTitle>
						<ResponsiveDialogDescription>
							{t("setDetail.deleteConfirm.body", { name: set?.name ?? "" })}
						</ResponsiveDialogDescription>
					</ResponsiveDialogHeader>
					{!online ? <OfflineNotice text={tTrash("offline.action")} /> : null}
					<ResponsiveDialogFooter>
						<Button variant="ghost" onClick={() => setDeleteOpen(false)} className="cursor-pointer">
							{t("generate.cancel")}
						</Button>
						<Button
							variant="destructive"
							disabled={deleteSet.isPending || !online}
							onClick={() => {
								if (!online) {
									toast.error(tTrash("offline.action"));
									return;
								}
								deleteSet.mutate(setId, {
									onSuccess: () => {
										setDeleteOpen(false);
										goBack();
									},
									onError: () => toast.error(t("library.errorTitle")),
								});
							}}
							className="cursor-pointer"
						>
							{t("setDetail.menu.delete")}
						</Button>
					</ResponsiveDialogFooter>
				</ResponsiveDialogContent>
			</ResponsiveDialog>

			<CardEditorDialog
				open={editorOpen}
				onOpenChange={setEditorOpen}
				card={editorCard}
				setId={setId}
			/>
			<GenerateDialog
				open={generateOpen}
				onOpenChange={setGenerateOpen}
				presetMaterialId={set?.sourceMaterialId ?? undefined}
			/>
		</div>
	);
}
